import {
  AlertCircle,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  ChevronsUpDown,
  MoreHorizontal,
  SearchX,
} from 'lucide-react';
import * as React from 'react';
import { cn } from '../../lib/utils';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './dropdown-menu';

export interface DataTableColumn<T extends object> {
  key: string;
  header: string;
  sortable?: boolean;
  align?: 'left' | 'center' | 'right';
  className?: string;
  render: (row: T) => React.ReactNode;
}

export interface DataTableRowAction<T extends object> {
  label: string;
  icon?: React.ReactNode;
  onClick: (row: T) => void;
  destructive?: boolean;
}

export interface DataTablePagination {
  page: number;
  limit: number;
  total?: number;
  onPageChange: (page: number) => void;
}

export interface DataTableSort {
  key: string;
  direction: 'asc' | 'desc';
}

export interface DataTableProps<T extends object> {
  title?: string;
  description?: string;
  headerActions?: React.ReactNode;
  filters?: React.ReactNode;
  columns: DataTableColumn<T>[];
  data: T[] | undefined;
  keyField: keyof T;
  isLoading?: boolean;
  isError?: boolean;
  errorMessage?: string;
  emptyMessage?: string;
  onRowClick?: (row: T) => void;
  rowActions?: DataTableRowAction<T>[];
  selectable?: boolean;
  pagination?: DataTablePagination;
  sort?: DataTableSort;
  onSortChange?: (key: string, direction: 'asc' | 'desc') => void;
  /** Extra <tr> rendered at end of tbody — use for "add new row" forms */
  addRow?: React.ReactNode;
  /** Controlled expanded row id */
  expandedRowId?: string | null;
  /** Renders content inside expansion <tr> below the matched row */
  renderExpandedRow?: (row: T) => React.ReactNode;
  /** Rendered inside <tfoot> — use for summary/total rows */
  tableFooter?: React.ReactNode;
  /** When true, renders the table without the outer card wrapper */
  noCard?: boolean;
}

export function DataTable<T extends object>({
  title,
  description,
  headerActions,
  filters,
  columns,
  data,
  keyField,
  isLoading = false,
  isError = false,
  errorMessage = 'Failed to load data',
  emptyMessage = 'No data found',
  onRowClick,
  rowActions,
  selectable = false,
  pagination,
  sort,
  onSortChange,
  addRow,
  expandedRowId,
  renderExpandedRow,
  tableFooter,
  noCard = false,
}: DataTableProps<T>) {
  const [selectedIds, setSelectedIds] = React.useState<Set<string>>(new Set());

  const hasRowActions = !!rowActions?.length;
  const totalCols = columns.length + (selectable ? 1 : 0) + (hasRowActions ? 1 : 0);

  const totalPages =
    pagination?.total !== undefined && pagination.limit
      ? Math.ceil(pagination.total / pagination.limit)
      : undefined;

  const showingFrom = pagination ? (pagination.page - 1) * pagination.limit + 1 : 1;
  const showingTo =
    pagination?.total !== undefined
      ? Math.min(pagination.page * pagination.limit, pagination.total)
      : (data?.length ?? 0);

  const allIds = data?.map((r) => String(r[keyField])) ?? [];
  const allSelected = allIds.length > 0 && allIds.every((id) => selectedIds.has(id));
  const someSelected = allIds.some((id) => selectedIds.has(id));

  function toggleAll() {
    if (allSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(allIds));
  }

  function toggleRow(id: string) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function handleSort(key: string) {
    if (!onSortChange) return;
    if (sort?.key === key) {
      onSortChange(key, sort.direction === 'asc' ? 'desc' : 'asc');
    } else {
      onSortChange(key, 'asc');
    }
  }

  function getPageRange(): (number | 'ellipsis')[] {
    if (!totalPages) return [];
    const delta = 1;
    const pages: (number | 'ellipsis')[] = [];
    const left = Math.max(2, (pagination?.page ?? 0) - delta);
    const right = Math.min(totalPages - 1, (pagination?.page ?? 0) + delta);
    pages.push(1);
    if (left > 2) pages.push('ellipsis');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('ellipsis');
    if (totalPages > 1) pages.push(totalPages);
    return pages;
  }

  const alignClass = {
    left: 'text-left',
    center: 'text-center',
    right: 'text-right',
  };

  const showHeader = !!(title || description || headerActions);

  const inner = (
    <>
      {/* Card header */}
      {showHeader && (
        <div className="flex items-start justify-between border-b border-gray-100 px-6 py-5">
          <div>
            {title && <h2 className="text-base font-semibold text-gray-900">{title}</h2>}
            {description && <p className="mt-0.5 text-sm text-gray-500">{description}</p>}
          </div>
          {headerActions && <div className="flex items-center gap-2 shrink-0">{headerActions}</div>}
        </div>
      )}

      {filters && (
        <>
          {showHeader && <div className="border-t border-gray-100" />}
          <div className="flex items-center px-6 py-4">{filters}</div>
        </>
      )}

      {filters && <div className="border-t border-gray-100" />}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50/50">
              {selectable && (
                <th className="w-10 px-6 py-4">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => {
                      if (el) el.indeterminate = someSelected && !allSelected;
                    }}
                    onChange={toggleAll}
                    className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                  />
                </th>
              )}
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={cn(
                    'px-6 py-4 text-xs font-semibold uppercase tracking-wider text-gray-500',
                    alignClass[col.align ?? 'left'],
                    col.sortable && 'cursor-pointer select-none hover:text-gray-700',
                    col.className,
                  )}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  <div
                    className={cn(
                      'inline-flex items-center gap-1',
                      col.align === 'center' && 'justify-center',
                      col.align === 'right' && 'justify-end',
                    )}
                  >
                    {col.header}
                    {col.sortable &&
                      (sort?.key === col.key ? (
                        sort.direction === 'asc' ? (
                          <ChevronUp size={13} />
                        ) : (
                          <ChevronDown size={13} />
                        )
                      ) : (
                        <ChevronsUpDown size={13} className="text-gray-400" />
                      ))}
                  </div>
                </th>
              ))}
              {hasRowActions && <th className="w-14 px-6 py-4" />}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 bg-white">
            {isLoading &&
              [...Array(5)].map((_, i) => (
                <tr key={i}>
                  {[...Array(totalCols)].map((_, j) => (
                    <td key={j} className="px-6 py-4">
                      <div
                        className="h-4 rounded bg-gray-100 animate-pulse"
                        style={{ width: `${50 + (((i + j) * 11) % 40)}%` }}
                      />
                    </td>
                  ))}
                </tr>
              ))}

            {isError && !isLoading && (
              <tr>
                <td colSpan={totalCols} className="px-6 py-14 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-error-50">
                      <AlertCircle size={20} className="text-error-500" />
                    </span>
                    <p className="text-sm font-medium text-gray-700">{errorMessage}</p>
                    <p className="text-xs text-gray-400">Please try again or contact support.</p>
                  </div>
                </td>
              </tr>
            )}

            {!isLoading && !isError && data?.length === 0 && !addRow && (
              <tr>
                <td colSpan={totalCols} className="px-6 py-14 text-center">
                  <div className="flex flex-col items-center gap-2">
                    <span className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
                      <SearchX size={20} className="text-gray-400" />
                    </span>
                    <p className="text-sm font-medium text-gray-700">{emptyMessage}</p>
                    <p className="text-xs text-gray-400">Try adjusting your filters or search.</p>
                  </div>
                </td>
              </tr>
            )}

            {!isLoading &&
              !isError &&
              data?.map((row) => {
                const id = String(row[keyField]);
                const isExpanded = expandedRowId === id;
                return (
                  <React.Fragment key={id}>
                    <tr
                      onClick={() => onRowClick?.(row)}
                      className={cn(
                        'group transition-all duration-150',
                        onRowClick && 'cursor-pointer hover:bg-gray-50',
                      )}
                    >
                      {selectable && (
                        <td
                          className="w-10 px-6 py-4"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleRow(id);
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedIds.has(id)}
                            onChange={() => toggleRow(id)}
                            className="h-4 w-4 rounded border-gray-300 cursor-pointer"
                          />
                        </td>
                      )}
                      {columns.map((col) => (
                        <td
                          key={col.key}
                          className={cn(
                            'px-6 py-4',
                            alignClass[col.align ?? 'left'],
                            col.className,
                          )}
                        >
                          {col.render(row)}
                        </td>
                      ))}
                      {hasRowActions && (
                        <td
                          className="w-14 px-6 py-4 text-right"
                          onClick={(e) => e.stopPropagation()}
                        >
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button
                                type="button"
                                className="inline-flex items-center justify-center h-8 w-8 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all opacity-0 group-hover:opacity-100 focus:opacity-100"
                              >
                                <MoreHorizontal size={16} />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              {rowActions?.map((action, idx) => (
                                <DropdownMenuItem
                                  key={idx}
                                  onClick={() => action.onClick(row)}
                                  className={cn(
                                    action.destructive &&
                                      'text-red-600 focus:text-red-600 focus:bg-red-50',
                                  )}
                                >
                                  {action.icon}
                                  {action.label}
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      )}
                    </tr>
                    {isExpanded && renderExpandedRow && (
                      <tr>
                        <td colSpan={totalCols} className="bg-gray-50 px-5 py-4">
                          {renderExpandedRow(row)}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}

            {addRow}
          </tbody>
          {tableFooter && <tfoot>{tableFooter}</tfoot>}
        </table>
      </div>

      {/* Pagination */}
      {pagination && (
        <>
          <div className="border-t border-gray-100" />
          <div className="flex items-center justify-between px-6 py-4">
            <span className="text-sm text-gray-500">
              {pagination.total !== undefined
                ? `Showing ${showingFrom} to ${showingTo} of ${pagination.total}`
                : `Page ${pagination.page}`}
            </span>
            <div className="flex items-center gap-1">
              <button
                type="button"
                onClick={() => pagination.onPageChange(pagination.page - 1)}
                disabled={pagination.page <= 1}
                className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft size={16} />
              </button>

              {totalPages
                ? getPageRange().map((p, idx) =>
                    p === 'ellipsis' ? (
                      <span key={`ell-${idx}`} className="px-1.5 text-sm text-gray-400">
                        …
                      </span>
                    ) : (
                      <button
                        key={p}
                        type="button"
                        onClick={() => pagination.onPageChange(p as number)}
                        className={cn(
                          'inline-flex items-center justify-center h-9 w-9 rounded-lg text-sm font-medium transition-colors',
                          p === pagination.page
                            ? 'bg-brand-500 text-white shadow-sm'
                            : 'border border-gray-200 text-gray-600 hover:bg-gray-50',
                        )}
                      >
                        {p}
                      </button>
                    ),
                  )
                : null}

              <button
                type="button"
                onClick={() => pagination.onPageChange(pagination.page + 1)}
                disabled={
                  totalPages
                    ? pagination.page >= totalPages
                    : !data || data.length < pagination.limit
                }
                className="inline-flex items-center justify-center h-9 w-9 rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );

  if (noCard) return <>{inner}</>;
  return (
    <div className="rounded-2xl border border-border bg-card shadow-sm overflow-hidden">
      {inner}
    </div>
  );
}
